import { useEffect, useState } from "react";
import { Eye, Images, Armchair, Music, CheckCircle2, XCircle, Clock, Download, Camera, Video } from "lucide-react";
import { api } from "@/lib/api";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border rounded-2xl p-5" style={{ borderColor: "#e3ddd2" }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} style={{ color: "var(--wed-primary)" }} />
        <p className="text-xs text-neutral-500">{label}</p>
      </div>
      <p className="font-wed text-3xl">{value}</p>
    </div>
  );
}

export default function StatsTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/owner/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (stats === null) {
    return <div className="animate-pulse font-wed text-xl py-16 text-center" style={{ color: "var(--wed-primary)" }}>Caricamento…</div>;
  }
  if (!stats) {
    return <p className="text-sm text-neutral-400 py-16 text-center">Impossibile caricare le statistiche.</p>;
  }

  const maxDay = Math.max(1, ...stats.page_views_by_day.map((d) => d.n));

  return (
    <div data-testid="stats-tab">
      <div className="mb-6">
        <h2 className="font-wed text-2xl">Statistiche</h2>
        <p className="text-sm text-neutral-500 mt-1">Quante persone sono passate dalla pagina, chi ha scaricato foto/video e quante. Nessun dato personale: solo conteggi.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Eye} label="Visite alla pagina" value={stats.page_views} />
        <StatCard icon={Images} label="Visite all'album" value={stats.album_views} />
        <StatCard icon={Armchair} label={'Ricerche "il mio tavolo"'} value={stats.table_searches} />
        <StatCard icon={Music} label="Canzoni richieste" value={stats.song_requests} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Download} label="File scaricati" value={stats.files_downloaded} />
        <StatCard icon={Camera} label="Foto caricate" value={stats.photos_uploaded} />
        <StatCard icon={Video} label="Video caricati" value={stats.videos_uploaded} />
        <StatCard
          icon={Images}
          label="Chi visita l'album scarica"
          value={stats.album_views > 0 ? `${Math.round((stats.downloads_single + stats.downloads_zip) / stats.album_views * 100)}%` : "—"}
        />
      </div>

      <div className="bg-white border rounded-2xl p-6 mb-8" style={{ borderColor: "#e3ddd2" }}>
        <p className="text-sm font-medium mb-4">Visite negli ultimi 14 giorni</p>
        <div className="flex items-end gap-1.5 h-32">
          {stats.page_views_by_day.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5" title={`${d.day}: ${d.n}`}>
              <div
                className="w-full rounded-t-sm transition-all duration-300"
                style={{ height: `${Math.max(3, (d.n / maxDay) * 100)}%`, background: d.n > 0 ? "var(--wed-primary)" : "#eee8dd" }}
              />
              <span className="text-[0.6rem] text-neutral-400">{d.day.slice(8, 10)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-6" style={{ borderColor: "#e3ddd2" }}>
        <p className="text-sm font-medium mb-4">Risposte RSVP</p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <CheckCircle2 size={18} className="mx-auto mb-1.5 text-emerald-600" />
            <p className="font-wed text-2xl">{stats.rsvp_confermato}</p>
            <p className="text-xs text-neutral-400">Confermati</p>
          </div>
          <div>
            <XCircle size={18} className="mx-auto mb-1.5 text-rose-500" />
            <p className="font-wed text-2xl">{stats.rsvp_rifiutato}</p>
            <p className="text-xs text-neutral-400">Rifiutati</p>
          </div>
          <div>
            <Clock size={18} className="mx-auto mb-1.5 text-neutral-400" />
            <p className="font-wed text-2xl">{stats.rsvp_in_attesa}</p>
            <p className="text-xs text-neutral-400">In attesa</p>
          </div>
        </div>
      </div>
    </div>
  );
}
